import { MakeErrorClass } from 'fejl'
import { Observable } from 'rxjs'

/**
 * Cancellation token.
 */
export interface CancellationToken {
  /**
   * Whether or not the token is marked as cancelled.
   */
  readonly isCancelled: boolean
  /**
   * Cancellation promise.
   * DO NOT USE WITH `Promise.race()`, rather use `CancellationToken.race` as it won't leak.
   */
  readonly cancellation: Promise<void>

  /**
   * Creates an Observable that emits a single value when the token is cancelled, then completes.
   * If the token is already cancelled, immediately emits and completes.
   */
  asObservable(): Observable<unknown>

  /**
   * Returns an AbortSignal for the cancellation token.
   */
  asAbortSignal(): AbortSignal

  /**
   * Throws if the token has been marked as cancelled.
   *
   * @param msg the message to use when throwing, if specified.
   */
  throwIfCancelled(msg?: string): void | never

  /**
   * Registers a callback that gets called when the token is
   * marked as cancelled.
   *
   * @returns {Function} disposer function which de-registers the callback.
   */
  onCancel(callback: () => unknown): () => unknown

  /**
   * Registers a callback that gets called when the source
   * is disposed.
   *
   * @returns {Function} disposer function which de-registers the callback.
   */
  onDispose(callback: () => unknown): () => unknown

  /**
   * Like `Promise.race([promise, ct.cancellation])`, but does not leak memory.
   *
   * @param promise
   */
  race<T>(promise: Promise<T>): Promise<T | undefined>
}

/**
 * Cancellation token statics.
 */
export const CancellationToken = {
  /**
   * The default cancellation token which is never cancelled and disposes right away.
   */
  default: createCancellationToken(
    () => false,
    () => () => undefined,
    invokeOnRegister,
  ),
  /**
   * A token that is already cancelled and disposes right away.
   */
  cancelled: createCancellationToken(
    () => true,
    invokeOnRegister,
    invokeOnRegister,
  ),
  /**
   * Combines cancellation tokens into a new one.
   * When any of the tokens are cancelled, so is the new one.
   * If any of the tokens are already cancelled, the new token
   * will be cancelled straight out the gate.
   */
  combine: (...tokens: CancellationToken[]): CancellationToken => {
    const source = createCancellationTokenSource()
    if (tokens.some((t) => t.isCancelled)) {
      source.cancel()
    } else {
      tokens.forEach((t) => t.onCancel(source.cancel))
    }
    return source.token
  },
}

/**
 * Cancellation token source.
 */
export interface CancellationTokenSource {
  /**
   * The cancellation token.
   */
  readonly token: CancellationToken

  /**
   * Adds a dispose handler. Used for cleaning up
   *
   * @param handler
   */
  onDispose(handler: () => unknown): () => unknown

  /**
   * Marks the token as cancelled.
   */
  cancel(): void

  /**
   * Disposes the source and cleans up all handlers.
   */
  dispose(): void
}

/**
 * Cancelled error class.
 *
 * Thrown when the operation has been cancelled and `throwIfCancelled` is called.
 */
export class Cancelled extends MakeErrorClass('The operation was cancelled.') {}

/**
 * Creates a cancellation token source.
 */
export function createCancellationTokenSource(): CancellationTokenSource {
  let cancelled = false
  let disposed = false
  let cancelHandlers: (() => unknown)[] = []
  let disposeHandlers: (() => unknown)[] = []

  const token = createCancellationToken(
    () => cancelled,
    addCancellationHandler,
    addDisposeHandler,
  )

  return {
    cancel,
    dispose,
    token,
    onDispose: addDisposeHandler,
  }

  /**
   * Cancels the token.
   */
  function cancel() {
    if (!cancelled && !disposed) {
      cancelled = true
      cancelHandlers.forEach((h) => h())
      dispose()
    }
  }

  /**
   * Disposes of all cancellation and dispose handlers.
   */
  function dispose() {
    disposed = true
    cancelHandlers = []

    const disposeRefs = disposeHandlers
    disposeHandlers = []
    disposeRefs.forEach((h) => h())
  }

  /**
   * Adds a cancellation handler.
   *
   * @param {*} handler
   */
  function addCancellationHandler(handler: () => unknown) {
    if (disposed) {
      if (cancelled) {
        handler()
      }
      return noop
    }

    cancelHandlers.push(handler)
    return () => {
      const idx = cancelHandlers.indexOf(handler)
      if (idx !== -1) {
        cancelHandlers.splice(idx, 1)
      }
    }
  }

  /**
   * Adds a dispose handler.
   *
   * @param {*} handler
   */
  function addDisposeHandler(handler: () => unknown) {
    if (disposed) {
      handler()
      return noop
    }

    disposeHandlers.push(handler)
    return () => {
      const idx = disposeHandlers.indexOf(handler)
      if (idx !== -1) {
        disposeHandlers.splice(idx, 1)
      }
    }
  }
}

/**
 * Creates a cancellation token.
 *
 * Used by `CancellationTokenSource` to create tokens by passing in the control functions.
 *
 * @param isCancelled a function for getting whether the token has been cancelled.
 * @param onCancel registers a cancellation handler, and returns a function for de-registering it.
 * @param onDispose registers a dispose handler, and returns a function for de-registering it.
 */
function createCancellationToken(
  isCancelled: () => boolean,
  onCancel: (fn: () => unknown) => () => void,
  onDispose: (fn: () => unknown) => () => void,
): CancellationToken {
  const cancellation = new Promise<void>(onCancel)
  const cancelled$ = new Observable((observer) => {
    const removeCancelHandler = onCancel(() => {
      observer.next()
    })

    const removeDisposeHandler = onDispose(() => {
      observer.complete()
    })

    return () => {
      removeCancelHandler()
      removeDisposeHandler()
    }
  })
  return {
    cancellation,
    onCancel,
    onDispose,
    throwIfCancelled,
    asObservable,
    asAbortSignal,
    race,
    get isCancelled() {
      // By making the creator of the token responsible for the cancellation state,
      // it can't be manipulated on the token directly.
      return isCancelled()
    },
  }

  function throwIfCancelled(msg?: string) {
    if (isCancelled()) {
      throw new Cancelled(msg)
    }
  }

  function asObservable() {
    return cancelled$
  }

  function asAbortSignal() {
    const controller = new AbortController()
    onCancel(controller.abort.bind(controller))
    return controller.signal
  }

  function race<T>(promise: Promise<T>): Promise<T | undefined> {
    return new Promise<T | undefined>((resolve, reject) => {
      const dispose = onCancel(() => resolve(undefined))
      promise.then(
        (r) => {
          dispose()
          resolve(r)
        },
        (err) => {
          reject(err)
          dispose()
        },
      )
    })
  }
}

/**
 * Invokes the callback when registered. Used
 * for the predefined cancellation tokens
 * @param cb
 */
function invokeOnRegister(cb: () => unknown) {
  cb()
  return noop
}

/**
 * No-op.
 */
function noop() {
  //
}
